import { apiClient } from '@/shared/api';
import type {
  BitrixCallListParams,
  BitrixCallListResponse,
  BitrixTouchSendListParams,
  BitrixTouchSendListResponse,
  BitrixTouchSettings,
  BitrixTouchSettingsRequest,
  C1BotCandidateListParams,
  C1BotCandidateListResponse,
  C1BotLoadListParams,
  C1BotLoadListResponse,
  C1BotLoadRequest,
  C1BotLoadResponse,
  C1ToAsrCallListResponse,
  C1ToAsrCallsParams,
  C1ToAsrTranscriptionRequest,
  C1ToAsrTranscriptionResponse,
  C2EddyCandidateListParams,
  C2EddyCandidateListResponse,
  C2EddyLoadListParams,
  C2EddyLoadListResponse,
  C2EddyLoadRequest,
  C2EddyLoadResponse,
  C3AsrDemoLoadListParams,
  C3AsrDemoLoadListResponse,
  C3AsrDemoLoadRequest,
  C3AsrDemoLoadResponse,
  IntegrationListParams,
  IntegrationListResponse,
  IntegrationMockModeRequest,
  IntegrationMockModeResponse,
  IntegrationRequestLogParams,
  IntegrationRequestLogResponse,
  IntegrationResponse,
  IntegrationScenarioRunRequest,
  IntegrationScenarioRunResponse,
  IntegrationScenariosResponse,
  IntegrationStatisticsParams,
  IntegrationStatisticsResponse,
  IntegrationUpdateRequest,
} from './types';

// Re-export для удобства потребителей
export type {
  BitrixCallListItem,
  BitrixCallListParams,
  BitrixCallListResponse,
  BitrixDateRange,
  BitrixTouchPredicate,
  BitrixTouchSendItem,
  BitrixTouchSendListParams,
  BitrixTouchSendListResponse,
  BitrixTouchSendStatus,
  BitrixTouchSettings,
  BitrixTouchSettingsRequest,
  C1BotCandidateItem,
  C1BotCandidateListParams,
  C1BotCandidateListResponse,
  C1BotLoadItem,
  C1BotLoadListParams,
  C1BotLoadListResponse,
  C1BotLoadRequest,
  C1BotLoadResponse,
  C1BotLoadStatus,
  C1ToAsrCallItem,
  C1ToAsrCallListResponse,
  C1ToAsrCallsParams,
  C1ToAsrTranscriptionRequest,
  C1ToAsrTranscriptionResponse,
  C2EddyCandidateItem,
  C2EddyCandidateListParams,
  C2EddyCandidateListResponse,
  C2EddyLoadItem,
  C2EddyLoadListParams,
  C2EddyLoadListResponse,
  C2EddyLoadRequest,
  C2EddyLoadResponse,
  C2EddyLoadRunResult,
  C2EddyLoadStatus,
  C3AsrDemoLoadItem,
  C3AsrDemoLoadListParams,
  C3AsrDemoLoadListResponse,
  C3AsrDemoLoadRequest,
  C3AsrDemoLoadResponse,
  C3AsrDemoLoadStatus,
  IntegrationConnection,
  IntegrationEnvVariable,
  IntegrationListItem,
  IntegrationListParams,
  IntegrationListResponse,
  IntegrationLogStatus,
  IntegrationMockModeRequest,
  IntegrationMockModeResponse,
  IntegrationMode,
  IntegrationRequestLogItem,
  IntegrationRequestLogParams,
  IntegrationRequestLogResponse,
  IntegrationResponse,
  IntegrationScenario,
  IntegrationScenarioRunRequest,
  IntegrationScenarioRunResponse,
  IntegrationScenarioStepResult,
  IntegrationScenariosResponse,
  IntegrationStatisticsByMethod,
  IntegrationStatisticsError,
  IntegrationStatisticsParams,
  IntegrationStatisticsResponse,
  IntegrationUpdateRequest,
} from './types';

/**
 * Получить список интеграций (GET /integration)
 */
export const fetchIntegrations = async (params?: IntegrationListParams): Promise<IntegrationListResponse> => {
  const { data } = await apiClient.get<IntegrationListResponse>('/integration', {
    params,
  });
  return data;
};

/**
 * Получить сведения об одной интеграции (GET /integration/{code})
 */
export const fetchIntegration = async (code: string): Promise<IntegrationResponse> => {
  const { data } = await apiClient.get<IntegrationResponse>(`/integration/${code}`);
  return data;
};

/**
 * Обновить сведения об интеграции (PUT /integration/{code})
 */
export const updateIntegration = async (
  code: string,
  payload: IntegrationUpdateRequest,
): Promise<IntegrationResponse> => {
  const { data } = await apiClient.put<IntegrationResponse>(`/integration/${code}`, payload);
  return data;
};

/**
 * Получить список звонков из Bitrix24 (GET /c2/bitrix/audiofile).
 *
 * Структурированные диапазоны `dateFrom` / `dateTo` распаковываются
 * в плоские query-параметры `dateFrom.at`, `dateFrom.to`, `dateTo.at`, `dateTo.to`.
 * Контракт эндпоинта задаёт эти имена как есть, поэтому приведение к kebab-case отключено.
 */
export const fetchBitrixCalls = async (params?: BitrixCallListParams): Promise<BitrixCallListResponse> => {
  const queryParams: Record<string, string | number> = {};

  if (params?.limit !== undefined) {
    queryParams.limit = params.limit;
  }
  if (params?.offset !== undefined) {
    queryParams.offset = params.offset;
  }
  if (params?.dateFrom?.from) {
    queryParams['dateFrom.at'] = params.dateFrom.from;
  }
  if (params?.dateFrom?.to) {
    queryParams['dateFrom.to'] = params.dateFrom.to;
  }
  if (params?.dateTo?.from) {
    queryParams['dateTo.at'] = params.dateTo.from;
  }
  if (params?.dateTo?.to) {
    queryParams['dateTo.to'] = params.dateTo.to;
  }

  const { data } = await apiClient.get<BitrixCallListResponse>('/c2/bitrix/audiofile', {
    params: queryParams,
    preserveParamsCase: true,
  });
  return data;
};

/**
 * Переключить mock-режим интеграции (PUT /integration/{code}/mock-mode).
 */
export const updateIntegrationMockMode = async (
  code: string,
  payload: IntegrationMockModeRequest,
): Promise<IntegrationMockModeResponse> => {
  const { data } = await apiClient.put<IntegrationMockModeResponse>(`/integration/${code}/mock-mode`, payload);
  return data;
};

/**
 * Получить статистику запросов интеграции (GET /integration/{code}/statistics).
 */
export const fetchIntegrationStatistics = async (
  code: string,
  params?: IntegrationStatisticsParams,
): Promise<IntegrationStatisticsResponse> => {
  const { data } = await apiClient.get<IntegrationStatisticsResponse>(`/integration/${code}/statistics`, { params });
  return data;
};

/**
 * Получить журнал запросов интеграции (GET /integration/{code}/request-log).
 */
export const fetchIntegrationRequestLog = async (
  code: string,
  params?: IntegrationRequestLogParams,
): Promise<IntegrationRequestLogResponse> => {
  const { data } = await apiClient.get<IntegrationRequestLogResponse>(`/integration/${code}/request-log`, { params });
  return data;
};

/**
 * Получить список тестовых сценариев интеграции (GET /integration/{code}/scenario).
 */
export const fetchIntegrationScenarios = async (code: string): Promise<IntegrationScenariosResponse> => {
  const { data } = await apiClient.get<IntegrationScenariosResponse>(`/integration/${code}/scenario`);
  return data;
};

/**
 * Запустить тестовый сценарий интеграции
 * (POST /integration/{code}/scenario/{scenarioCode}/run).
 */
export const runIntegrationScenario = async (
  code: string,
  scenarioCode: string,
  payload: IntegrationScenarioRunRequest = {},
): Promise<IntegrationScenarioRunResponse> => {
  const { data } = await apiClient.post<IntegrationScenarioRunResponse>(
    `/integration/${code}/scenario/${scenarioCode}/run`,
    payload,
  );
  return data;
};

/**
 * Запустить загрузку готовых ASR-транскрибаций из внешней очереди
 * (POST /c3/asr-demo/load).
 *
 * `silentError` подавляет общее всплывающее уведомление: отказ по условию отбора
 * показывает сам блок загрузки — вместе с причиной, пришедшей от источника.
 */
export const startC3AsrDemoLoad = async (payload: C3AsrDemoLoadRequest = {}): Promise<C3AsrDemoLoadResponse> => {
  const { data } = await apiClient.post<C3AsrDemoLoadResponse>('/c3/asr-demo/load', payload, { silentError: true });
  return data;
};

/**
 * Получить журнал загрузок ASR-транскрибаций (GET /c3/asr-demo/load).
 */
export const fetchC3AsrDemoLoads = async (params?: C3AsrDemoLoadListParams): Promise<C3AsrDemoLoadListResponse> => {
  const { data } = await apiClient.get<C3AsrDemoLoadListResponse>('/c3/asr-demo/load', { params });
  return data;
};

/**
 * Получить разговоры-кандидаты на загрузку по интервалу времени начала
 * (GET /c1/bot/candidates) — ничего не загружает, только возвращает список.
 */
export const fetchC1BotCandidates = async (params?: C1BotCandidateListParams): Promise<C1BotCandidateListResponse> => {
  const { data } = await apiClient.get<C1BotCandidateListResponse>('/c1/bot/candidates', { params });
  return data;
};

/**
 * Отобрать звонки для отправки на транскрибацию (GET /c1/to-asr/call).
 *
 * Границы интервала передаются по общей конвенции проекта (`date-from` / `date-to`) —
 * приведение к kebab-case выполняет `apiClient`.
 */
export const fetchC1ToAsrCalls = async (params: C1ToAsrCallsParams): Promise<C1ToAsrCallListResponse> => {
  const { data } = await apiClient.get<C1ToAsrCallListResponse>('/c1/to-asr/call', { params });
  return data;
};

/**
 * Запустить прогрузку выбранных диалогов голосового бота из внешнего источника
 * (POST /c1/bot/load).
 */
export const startC1BotLoad = async (payload: C1BotLoadRequest): Promise<C1BotLoadResponse> => {
  const { data } = await apiClient.post<C1BotLoadResponse>('/c1/bot/load', payload);
  return data;
};

/**
 * Получить журнал прогрузки диалогов голосового бота (GET /c1/bot/load).
 */
export const fetchC1BotLoads = async (params?: C1BotLoadListParams): Promise<C1BotLoadListResponse> => {
  const { data } = await apiClient.get<C1BotLoadListResponse>('/c1/bot/load', { params });
  return data;
};

/**
 * Получить число закрытых обращений HelpDeskEddy за период и их страницу (GET /c2/eddy/candidates) —
 * ничего не загружает.
 *
 * `silentError` подавляет общее всплывающее уведомление: отказ поиска (обратный период, недоступный
 * источник) называет сам блок загрузки вместе с причиной от сервера.
 */
export const fetchC2EddyCandidates = async (
  params: C2EddyCandidateListParams,
): Promise<C2EddyCandidateListResponse> => {
  const { data } = await apiClient.get<C2EddyCandidateListResponse>('/c2/eddy/candidates', {
    params,
    silentError: true,
  });
  return data;
};

/**
 * Запустить загрузку всех закрытых обращений периода (POST /c2/eddy/load).
 *
 * `silentError` подавляет общее всплывающее уведомление: отказ запуска называет сам блок загрузки
 * вместе с причиной от сервера.
 */
export const startC2EddyLoad = async (payload: C2EddyLoadRequest): Promise<C2EddyLoadResponse> => {
  const { data } = await apiClient.post<C2EddyLoadResponse>('/c2/eddy/load', payload, { silentError: true });
  return data;
};

/**
 * Получить страницу журнала загрузок обращений HelpDeskEddy (GET /c2/eddy/load).
 */
export const fetchC2EddyLoads = async (params?: C2EddyLoadListParams): Promise<C2EddyLoadListResponse> => {
  const { data } = await apiClient.get<C2EddyLoadListResponse>('/c2/eddy/load', { params });
  return data;
};

/**
 * Отправить выбранные звонки на транскрибацию (POST /c1/to-asr/transcription).
 */
export const startC1ToAsrTranscription = async (
  payload: C1ToAsrTranscriptionRequest,
): Promise<C1ToAsrTranscriptionResponse> => {
  const { data } = await apiClient.post<C1ToAsrTranscriptionResponse>('/c1/to-asr/transcription', payload);
  return data;
};

/**
 * Получить настройки режима касания Bitrix24 (GET /c2/bitrix/touch).
 */
export const fetchBitrixTouchSettings = async (): Promise<BitrixTouchSettings> => {
  const { data } = await apiClient.get<BitrixTouchSettings>('/c2/bitrix/touch');
  return data;
};

/**
 * Сохранить настройки режима касания Bitrix24 (PUT /c2/bitrix/touch).
 *
 * `silentError` подавляет общее всплывающее уведомление: причину отказа (чего не хватает
 * включаемому режиму, чем не разобрано условие) называет сам блок настроек.
 */
export const saveBitrixTouchSettings = async (payload: BitrixTouchSettingsRequest): Promise<BitrixTouchSettings> => {
  const { data } = await apiClient.put<BitrixTouchSettings>('/c2/bitrix/touch', payload, { silentError: true });
  return data;
};

/**
 * Получить страницу заведённых касаний Bitrix24 (GET /c2/bitrix/touch/send).
 */
export const fetchBitrixTouchSends = async (
  params?: BitrixTouchSendListParams,
): Promise<BitrixTouchSendListResponse> => {
  const { data } = await apiClient.get<BitrixTouchSendListResponse>('/c2/bitrix/touch/send', { params });
  return data;
};
